import { useState } from "react";
import { Badge, Button, Card, Empty, Modal, Space, Steps, Tooltip, Typography } from "antd";
import { CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
	allCriteriaSigned,
	getRtpRights,
	isActivePhase,
	isFinalPhase,
	isPhaseLocked,
	isPlanReadOnly,
	phaseDateMeta,
	phaseDurationDays,
} from "@utils/rtp_utils";
import { rtpApi, runRtpWrite, useCurrentUserUuid } from "./rtpHooks";
import { PhaseModal } from "./PhaseModal";
import { CriteriaList } from "./CriteriaList";
import type { RtpMetricProgress, RtpPhase, RtpPlanDetail } from "./types";

const { Paragraph } = Typography;

export interface PlanTabProps {
	plan: RtpPlanDetail;
	refetch: () => Promise<void>;
}

type StepStatus = "wait" | "process" | "finish";

function phaseStepStatus(plan: RtpPlanDetail, phase: RtpPhase): StepStatus {
	if (isPhaseLocked(phase)) return "finish";
	if (isActivePhase(plan, phase)) return "process";
	return "wait";
}

// Completed phases show their real duration (start → completedAt); everything else the planned one.
function phaseStepDescription(phase: RtpPhase, started: boolean) {
	const { startDate } = phase;
	const days = phaseDurationDays(phase);
	const duration = `${days} day${days === 1 ? "" : "s"}`;
	if (!startDate) return duration;
	return (
		<>
			{started ? "Started" : "Starts"} {dayjs(startDate).format("MMM D")}
			<br />
			{duration}
		</>
	);
}

// progressByKey: the metric-progress data PlanDetail prefetches, used for the "met on" reminders;
// onOpenProgress switches the plan to the Progress tab at one metric's card (the reminder's link).
export const OverviewTab = ({
	plan,
	refetch,
	progressByKey,
	onOpenProgress,
}: PlanTabProps & { progressByKey: Record<string, RtpMetricProgress>; onOpenProgress: (metricKey: string) => void }) => {
	const currentUserUuid = useCurrentUserUuid();
	const rights = getRtpRights(plan, currentUserUuid);
	const canManage = rights.isManager && !isPlanReadOnly(plan);

	const [modalPhase, setModalPhase] = useState<RtpPhase | null>(null);
	const [modalMode, setModalMode] = useState<"add" | "edit" | null>(null);

	const openAddModal = () => {
		setModalPhase(null);
		setModalMode("add");
	};
	const openEditModal = (phase: RtpPhase) => {
		setModalPhase(phase);
		setModalMode("edit");
	};
	const closeModal = () => {
		setModalMode(null);
		setModalPhase(null);
	};

	const handleDelete = (phase: RtpPhase) => {
		Modal.confirm({
			title: `Delete phase "${phase.name}"?`,
			content: "This can't be undone.",
			okText: "Delete",
			okType: "danger",
			okButtonProps: { "data-attr": "settings.rtp-phase.delete-submit" } as any,
			onOk: () => runRtpWrite(() => rtpApi.deleteRtpPhase(phase.id), refetch),
		});
	};

	const handleComplete = (phase: RtpPhase) => {
		Modal.confirm({
			title: "Complete this phase?",
			content: isFinalPhase(plan, phase)
				? "This is the final phase — completing it completes the plan."
				: "The next phase starts today.",
			okText: "Complete Phase",
			okButtonProps: { "data-attr": "settings.rtp-phase.complete-submit" } as any,
			onOk: () => runRtpWrite(() => rtpApi.rtpPhaseAction(phase.id, { action: "COMPLETE_PHASE" }), refetch),
		});
	};

	return (
		<div>
			{plan.phases.length > 0 ? (
				<div className="rtp-grid-wrap rtp-steps-wrap">
					<Steps
						className="rtp-steps"
						labelPlacement="vertical"
						style={{ minWidth: Math.max(plan.phases.length * 140, 100) }}
						items={plan.phases.map((phase) => {
							const status = phaseStepStatus(plan, phase);
							return {
								title: phase.name,
								description: phaseStepDescription(phase, status !== "wait"),
								status,
							};
						})}
					/>
				</div>
			) : null}

			{canManage ? (
				<div style={{ margin: "12px 0" }}>
					<Button
						color="primary"
						variant="solid"
						icon={<PlusOutlined />}
						data-attr="settings.rtp-phase.add"
						onClick={openAddModal}
					>
						Add Phase
					</Button>
				</div>
			) : null}

			{plan.phases.length === 0 ? (
				<Empty description="No phases yet." />
			) : (
				<Space direction="vertical" className="rtp-phase-list" style={{ width: "100%" }} size="middle">
					{plan.phases.map((phase, index) => {
						const showManagerControls = canManage && !isPhaseLocked(phase);
						const showComplete = canManage && plan.status === "active" && isActivePhase(plan, phase);
						const signed = allCriteriaSigned(phase);

						const completeButton = (
							<Tooltip
								title={signed ? "Complete phase" : "Every exit criteria in this phase must be signed off first."}
							>
								<span>
									<Button
										className={signed ? "rtp-complete-glow" : undefined}
										color="green"
										variant="solid"
										shape="circle"
										icon={<CheckOutlined />}
										aria-label="Complete phase"
										data-attr="settings.rtp-phase.complete"
										disabled={!signed}
										onClick={() => handleComplete(phase)}
									/>
								</span>
							</Tooltip>
						);
						const active = isActivePhase(plan, phase);

						const signedCount = phase.criteria.filter((c) => c.signedOffBy != null).length;
						const criteriaStat =
							phase.criteria.length > 0 ? (
								<div className={`rtp-crit-stat${signed ? " rtp-crit-stat--done" : ""}`}>
									<div className="rtp-crit-stat-num">
										<span className="rtp-crit-stat-signed">{signedCount}</span>/{phase.criteria.length}
									</div>
									<div className="rtp-crit-stat-label">exit criteria</div>
								</div>
							) : null;

						return (
							<Card
								key={phase.id}
								className={`rtp-phase-card${active ? " rtp-phase-card--active" : ""}`}
								title={
									<div className="rtp-phase-card-title">
										<span
											className={`rtp-phase-num${
												phase.completedAt ? " rtp-phase-num--done" : active ? " rtp-phase-num--active" : ""
											}`}
										>
											{phase.completedAt ? <CheckOutlined /> : index + 1}
										</span>
										<div>
											<Space align="center">
												<span>{phase.name}</span>
												{phase.completedAt ? (
													<span className="rtp-status-pill rtp-status-pill--completed">
														<CheckOutlined /> Completed
													</span>
												) : null}
												{active ? (
													<span className="rtp-status-pill rtp-status-pill--active">
														<Badge status="processing" /> Active
													</span>
												) : null}
											</Space>
											<div className="rtp-phase-card-meta">{phaseDateMeta(phase)}</div>
										</div>
									</div>
								}
								extra={
									criteriaStat || showManagerControls || showComplete ? (
										<Space size={12}>
											{criteriaStat}
											{showComplete ? completeButton : null}
											{showManagerControls ? (
												<Space size={8}>
													<Tooltip title="Edit phase">
														<Button
															color="primary"
															variant="solid"
															shape="circle"
															icon={<EditOutlined />}
															aria-label="Edit phase"
															data-attr="settings.rtp-phase.edit"
															onClick={() => openEditModal(phase)}
														/>
													</Tooltip>
													<Tooltip title="Delete phase">
														<Button
															color="danger"
															variant="solid"
															shape="circle"
															icon={<DeleteOutlined />}
															aria-label="Delete phase"
															data-attr="settings.rtp-phase.delete"
															onClick={() => handleDelete(phase)}
														/>
													</Tooltip>
												</Space>
											) : null}
										</Space>
									) : undefined
								}
							>
								{phase.goal ? <Paragraph className="rtp-pre-wrap">{phase.goal}</Paragraph> : null}
								<CriteriaList
									plan={plan}
									phase={phase}
									refetch={refetch}
									progressByKey={progressByKey}
									onOpenProgress={onOpenProgress}
								/>
							</Card>
						);
					})}
				</Space>
			)}

			<PhaseModal
				plan={plan}
				phase={modalMode === "edit" ? modalPhase : null}
				open={modalMode !== null}
				onClose={closeModal}
				refetch={refetch}
			/>
		</div>
	);
};
